<template>
    <form @submit.prevent="save">
        <template v-if="products">
            <div class="input-group mb-3">
                <div class="input-group-prepend">
                    <div class="input-group-text">
                        {{ $t("products.bundles.attributes.name") }}
                    </div>
                </div>
                <input
                    type="text"
                    class="form-control"
                    required
                    name="name"
                    v-model="theBundle.name"
                />
            </div>

            <hr />

            <template v-if="theBundle.products">
                <div
                    v-for="(product, productIdx) in theBundle.products"
                    :key="productIdx"
                >
                    <hr v-if="productIdx > 0" />

                    <template v-if="isSoloProductBundle">
                        <div class="d-flex justify-content-between">
                            <div>
                                <h6 class="font-weight-bold">
                                    {{ product.name }}
                                </h6>
                                <p class="mb-0">
                                    {{ $t("products.attributes.solo_product") }}
                                </p>
                            </div>

                            <button
                                type="button"
                                class="btn btn-secondary"
                                @click.prevent="removeProduct(productIdx)"
                            >
                                <i class="fal fa-trash"></i>
                            </button>
                        </div>
                    </template>
                    <template v-else>
                        <h6 class="font-weight-bold mb-2">
                            {{ product.name }}
                        </h6>
                        <div class="input-group mb-3">
                            <div class="input-group-prepend">
                                <div class="input-group-text">
                                    {{
                                        $t(
                                            "products.bundles.attributes.product_amount"
                                        )
                                    }}
                                </div>
                            </div>
                            <input
                                type="number"
                                required
                                min="1"
                                class="form-control"
                                v-model="product.amount"
                            />
                            <button
                                type="button"
                                class="btn btn-secondary"
                                @click.prevent="removeProduct(productIdx)"
                            >
                                <i class="fal fa-trash"></i>
                            </button>
                        </div>
                    </template>
                </div>
            </template>

            <hr />

            <products-search
                v-if="!isSoloProductBundle"
                :products="products"
                ignore-out-of-stock
                ignore-deleted
                :allow-solo-products="!hasProducts"
                :required="!hasProducts"
                @onAddProducts="addProducts"
                class="mb-3"
            ></products-search>

            <div
                v-if="successMessage"
                class="alert alert-success mb-3"
                role="alert"
            >
                {{ successMessage }}
            </div>
            <div
                v-if="errorMessage"
                class="alert alert-danger mb-3"
                role="alert"
            >
                {{ errorMessage }}
            </div>

            <button v-if="!saving" type="submit" class="btn btn-primary">
                {{
                    $t(
                        theBundle.id
                            ? "products.bundles.editor.button_save_existing"
                            : "products.bundles.editor.button_save_new"
                    )
                }}
            </button>
            <SbSpinner v-else></SbSpinner>
        </template>
        <SbSpinner v-else></SbSpinner>
    </form>
</template>

<style lang="scss" scoped></style>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs, watch } from "vue";
import axios from "axios";
import { downloadProducts } from "../downloadProducts";
import { useI18n } from "../../../composables/useI18n";
import { route } from "ziggy";
const props = defineProps({
  bundle: {
    type: Object,
    required: true
  }
});
const {
  bundle
} = toRefs(props);
const emit = defineEmits(["updated"]);
const {
  t
} = useI18n();
const theBundle = ref({});
const products = ref(null);
const soloProductIds = ref([]);
const saving = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);
const hasProducts = computed(() => {
  return theBundle.value?.products?.length > 0;
});
const isSoloProductBundle = computed(() => {
  return theBundle.value?.products?.some(prod => soloProductIds.value.includes(prod.id));
});
function addProducts(products) {
  products.forEach(prod => theBundle.value.products.push({
    id: prod.product.id,
    name: prod.product.name,
    amount: prod.amount
  }));
}
function removeProduct(idx) {
  theBundle.value.products.splice(idx, 1);
}
async function save() {
  if (saving.value) {
    return;
  }
  saving.value = true;
  errorMessage.value = null;
  successMessage.value = null;
  try {
    const response = theBundle.value.id ? await axios.put(route("products.bundles.update", theBundle.value.id), theBundle.value) : await axios.post(route("products.bundles.store"), theBundle.value);
    emit("updated", JSON.parse(JSON.stringify(response.data)));
    successMessage.value = t("products.bundles.editor.save_success");
  } catch (e) {
    if (console.warn) {
      console.warn(e);
    }
    errorMessage.value = t("products.bundles.editor.save_error");
  }
  saving.value = false;
}
watch(bundle, bundleValue => {
  const clonedBundle = JSON.parse(JSON.stringify(bundleValue));

  if (!clonedBundle.name) {
    clonedBundle.name = "";
  }

  if (!clonedBundle.products) {
    clonedBundle.products = [];
  }

  theBundle.value = clonedBundle;
}, {
  immediate: true,
  deep: true
});
onMounted(async () => {
  products.value = await downloadProducts(false);
  soloProductIds.value = products.value.filter(prod => prod.solo_product).map(prod => prod.id);
});
</script>
